// Runtime connection settings for the two independent control surfaces.
// Bases may have build-time defaults; bearer tokens are always operator-entered
// and stay in localStorage so they never land in the static frontend bundle.

const DEFAULT_TRACE_BASE = normalizeBase(
  (import.meta.env.VITE_TRACE_API_BASE as string | undefined) ?? "",
);
const DEFAULT_CONTROL_BASE = normalizeBase(
  (import.meta.env.VITE_SWARM_API_BASE as string | undefined) ?? "/api",
);

const TRACE_BASE_KEY = "swarm_trace_api_base";
const TRACE_TOKEN_KEY = "swarm_trace_read_token";
const CONTROL_BASE_KEY = "swarm_control_api_base";
const CONTROL_TOKEN_KEY = "swarm_control_token";
export const CONNECTIONS_STORAGE_KEY = "swarm.connections.v1";
const LEGACY_KEYS = [TRACE_BASE_KEY, TRACE_TOKEN_KEY, CONTROL_BASE_KEY, CONTROL_TOKEN_KEY];
const LEGACY_LOOPBACK_CONTROL_BASE = "http://127.0.0.1:7878";
export const CONNECTIONS_CHANGED_EVENT = "swarm-connections-changed";
// Settings are shared by tabs, while queries/composers are account-scoped.
// Notify those consumers for browser storage events as well as local saves.
if (typeof window !== "undefined") window.addEventListener("storage", event => {
  if (event.key === null || event.key === CONNECTIONS_STORAGE_KEY || LEGACY_KEYS.includes(event.key)) {
    window.dispatchEvent(new Event(CONNECTIONS_CHANGED_EVENT));
  }
});

export interface ConsoleConnections {
  traceBase: string;
  traceToken: string;
  controlBase: string;
  controlToken: string;
}

function disconnected(): ConsoleConnections {
  return { traceBase: DEFAULT_TRACE_BASE, traceToken: "", controlBase: DEFAULT_CONTROL_BASE, controlToken: "" };
}

/** One record binds both endpoint/token pairs. A failed setItem cannot leave
 * new addresses combined with old credentials. Legacy values are read only
 * until the first explicit save; never fall back after a corrupt new record. */
function readConnections(): { values: ConsoleConnections; error: string } {
  if (typeof window === "undefined") return { values: disconnected(), error: "" };
  try {
    const storage = window.localStorage;
    const raw = storage.getItem(CONNECTIONS_STORAGE_KEY);
    if (raw !== null) {
      const saved: unknown = JSON.parse(raw);
      if (!saved || typeof saved !== "object" || !("version" in saved) || saved.version !== 1 || !("values" in saved)) throw new Error("Invalid connection record");
      const values = saved.values;
      if (!values || typeof values !== "object" || !["traceBase", "traceToken", "controlBase", "controlToken"].every(key => key in values && typeof (values as Record<string, unknown>)[key] === "string")) throw new Error("Invalid connection values");
      const connection = values as ConsoleConnections;
      return { values: { traceBase: normalizeBase(connection.traceBase), traceToken: connection.traceToken, controlBase: normalizeBase(connection.controlBase), controlToken: connection.controlToken }, error: "" };
    }
    // Any failed read discards the entire legacy snapshot, not just its URL.
    const traceBase = storage.getItem(TRACE_BASE_KEY);
    const traceToken = storage.getItem(TRACE_TOKEN_KEY);
    const controlBase = storage.getItem(CONTROL_BASE_KEY);
    const controlToken = storage.getItem(CONTROL_TOKEN_KEY);
    return { values: {
      traceBase: normalizeBase(traceBase ?? DEFAULT_TRACE_BASE), traceToken: traceToken ?? "",
      controlBase: storedControlBase(controlBase), controlToken: controlToken ?? "",
    }, error: "" };
  } catch {
    return { values: disconnected(), error: "Saved connections could not be read safely. No saved tokens are being used. Re-enter and save both connections, or clear saved values." };
  }
}

export function getConsoleConnections(): ConsoleConnections { return readConnections().values; }
export function getConnectionStorageError(): string { return readConnections().error; }

export function setConsoleConnections(next: ConsoleConnections) {
  const values: ConsoleConnections = {
    traceBase: normalizeBase(next.traceBase), traceToken: next.traceToken.trim(),
    controlBase: normalizeBase(next.controlBase), controlToken: next.controlToken.trim(),
  };
  window.localStorage.setItem(CONNECTIONS_STORAGE_KEY, JSON.stringify({ version: 1, values }));
  window.dispatchEvent(new Event(CONNECTIONS_CHANGED_EVENT));
}

export function clearConsoleConnections() {
  // Keep an explicit empty record so leftover legacy credentials cannot revive
  // after a partial deletion or when another old tab writes its legacy keys.
  setConsoleConnections(disconnected());
  try {
    // Erase tokens first. Never remove an old address while its token remains.
    for (const key of [TRACE_TOKEN_KEY, CONTROL_TOKEN_KEY, TRACE_BASE_KEY, CONTROL_BASE_KEY]) window.localStorage.removeItem(key);
  } catch {
    throw new Error("Active connections were cleared, but older saved values could not be removed. Restore browser storage and clear again.");
  }
}

export function requestConnectionSettings() {
  window.dispatchEvent(new Event("swarm-open-connection-settings"));
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string): Promise<T> {
  const { traceBase, traceToken, controlToken } = getConsoleConnections();
  const token = traceToken || (!traceBase ? controlToken : "");
  if (!token) {
    throw new ApiError(401, "Trace read token required.");
  }
  const response = await fetch(`${traceBase}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      await response.text().catch(() => response.statusText),
    );
  }
  return (await response.json()) as T;
}

function normalizeBase(value: string): string {
  return value.trim().replace(/\/+$/, "");
}

function storedControlBase(value: string | null): string {
  const stored = normalizeBase(value ?? DEFAULT_CONTROL_BASE);
  if (
    stored === LEGACY_LOOPBACK_CONTROL_BASE &&
    new URL(stored).origin !== window.location.origin
  ) {
    try { window.localStorage.setItem(CONTROL_BASE_KEY, DEFAULT_CONTROL_BASE); } catch { /* Read-only storage: use the safe default in memory. */ }
    return DEFAULT_CONTROL_BASE;
  }
  return stored;
}

// ─── Server response types (mirror swarm-trace-server JSON) ───────────────

export interface TaskRow {
  id: string;
  name: string;
  created_at: string;
  last_seen_at: string;
  runs_count: number;
  active_runs: number;
}

export interface UsageStats {
  input_tokens?: number;
  output_tokens?: number;
  cache_creation_input_tokens?: number;
  cache_read_input_tokens?: number;
  turn_usage?: TurnUsage[];
}

export interface TurnUsage {
  index: number;
  input_tokens: number;
  output_tokens: number;
  cache_read_input_tokens?: number;
  cache_creation_input_tokens?: number;
}

export interface RunRow {
  id: string;
  task_id: string;
  agent_id: string;
  agent_name: string;
  node_id: string | null;
  graph_id: string | null;
  status: "running" | "succeeded" | "failed" | "cancelled";
  started_at: string | null;
  ended_at: string | null;
  error: string | null;
  usage: UsageStats | null;
}

export interface RunDetail extends RunRow {
  input: unknown;
  output: unknown;
}

export interface EventRow {
  seq: number;
  kind: string;
  body: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  at: string;
}
